import React, { useContext, useState } from 'react';
import { useNavigationBlock } from '../../lib/hook/router/useNavigationBlock';

interface IActionPlanContext {
  dirty: boolean;
  editorId: string | null;
  isGuestVisitor: boolean;
  setDirty: React.Dispatch<React.SetStateAction<boolean>>;
  setEditorId: React.Dispatch<React.SetStateAction<string | null>>;
}

const ActionPlanContext: React.Context<IActionPlanContext> =
  React.createContext<IActionPlanContext>({
    dirty: false,
    editorId: null,
    isGuestVisitor: false,
    setDirty: () => {},
    setEditorId: () => {},
  });

type ActionPlanContextProviderProps = React.PropsWithChildren<{
  isGuestVisitor: boolean;
}>;

/**
 * Context provider that only handles the dirty state. This must be
 * used inside room creation/edition flows because blocking the navigation
 * if there are unsaved changes enters in conflict with this behavior
 * from the room flow itself
 */
export const ActionPlanContextProvider: React.FC<
  ActionPlanContextProviderProps
> = (props: ActionPlanContextProviderProps): JSX.Element => {
  const { isGuestVisitor } = props;

  const [dirty, setDirty] = useState(false);
  const [editorId, setEditorId] = useState<string | null>(null);

  return (
    <ActionPlanContext.Provider
      value={{ dirty, editorId, isGuestVisitor, setDirty, setEditorId }}
    >
      {props.children}
    </ActionPlanContext.Provider>
  );
};

/**
 * Context provider that blocks the navigation if there are unsaved changes.
 * Use this whenever the MAPs feature is used "standalone" and there is
 * nothing else that could interfere with the blocking behavior
 */
type BlockingActionPlanContextProviderProps = React.PropsWithChildren<unknown>;

export const BlockingActionPlanContextProvider: React.FC<
  BlockingActionPlanContextProviderProps
> = (props: BlockingActionPlanContextProviderProps): JSX.Element => {
  const { dirty } = useContext(ActionPlanContext);

  // prompt for confirmation when navigating away with arrows or gestures
  useNavigationBlock(dirty, [], 'ActionPlanContext');

  return <>{props.children}</>;
};

export { ActionPlanContext };
